// src/pages/UseCasesPage.js
import React, { useEffect } from "react"; // Import useEffect
import { useLocation } from "react-router-dom"; // Import useLocation

import ContactForm from "../components/ContactForm";

// --- Import Framer Motion and AnimatedSection ---
import { motion } from "framer-motion";
import AnimatedSection, {
  fadeIn,
  fadeInUp,
  fadeInLeft,
  // fadeInRight,
  scaleUp,
  staggerItemFadeInUp,
} from "../components/AnimatedSection"; // Adjust path if needed

// Data for each Use Case
const useCasesData = [
  {
    id: "financial",
    title: "Innovative Financial Solutions",
    videoSrc: "https://zenithblox.blob.core.windows.net/videos/financial_services.mp4?sp=r&st=2025-06-11T15:34:06Z&se=2060-06-11T23:34:06Z&spr=https&sv=2024-11-04&sr=b&sig=BJPm4sl%2FOfm1OTXbkcrl2IVHOzNucWaYEG3f0093C2Y%3D",
    challenge:
      "Financial services face mounting pressure from outdated systems, complex regulations, and rising demands for secure, efficient fintech. Slow cross-border transactions and illiquid assets continue to create costly delays and hinder financial innovation and global market responsiveness.Navigating the intricate web of contemporary supply chains poses significant challenges in product tracking and authenticity verification. This lack of clarity can lead to operational inefficiencies, increased fraud risk, and the proliferation of counterfeit products.",
    approach:
      "ZenithBlox offers cutting-edge financial solutions tailored to tackle supply chain obstacles. Our platform enhances operational workflows, minimizes expenses, and fosters fintech advancements to promote transparency and efficiency throughout the supply chain.enithBlox offers cutting-edge financial solutions tailored to tackle supply chain obstacles. Our platform enhances operational workflows, minimizes expenses, and fosters fintech advancements to promote transparency and efficiency throughout the supply chain.",
  },
  {
    id: "supplychain",
    title: "Supply Chain",
    videoSrc: "https://zenithblox.blob.core.windows.net/videos/supply_chain.mp4?sp=r&st=2025-06-11T15:36:58Z&se=2060-06-11T23:36:58Z&spr=https&sv=2024-11-04&sr=b&sig=gvqHZHR6yY%2FuY0%2FngnhXHI4XqCaCuEQB7%2BDEuWnCias%3D",
    challenge:
      "Supply chains are frequently fragmented and lack visibility, making it difficult to track products accurately and verify their authenticity. This limited transparency can result in significant inefficiencies, increased risk of fraud, and the circulation of counterfeit goods.Navigating the intricate web of contemporary supply chains poses significant challenges in product tracking and authenticity verification. This lack of clarity can lead to operational inefficiencies, increased fraud risk, and the proliferation of counterfeit products.",
    approach:
      "By utilizing blockchain’s secure and immutable ledger, ZenithBlox improves end-to-end transparency and traceability, enabling real-time visibility and greater accountability throughout the entire supply chain.ZenithBlox offers cutting-edge financial solutions tailored to tackle supply chain obstacles. Our platform enhances operational workflows, minimizes expenses, and fosters fintech advancements to promote transparency and efficiency throughout the supply chain.",
  },
  {
    id: "healthcare",
    title: "Healthcare",
    videoSrc: "https://zenithblox.blob.core.windows.net/videos/healthcare.mp4?sp=r&st=2025-06-11T15:36:28Z&se=2060-06-11T23:36:28Z&spr=https&sv=2024-11-04&sr=b&sig=%2BwwcPhQ3lvhu86qSHQi3W1j160AXl8XZcIbsuTWGuKI%3D",
    challenge:
      "The healthcare industry struggles with securely managing and exchanging sensitive patient information. Disconnected systems, data silos, and growing privacy concerns often hinder effective collaboration and can negatively affect the quality of patient care and outcomes.Navigating the intricate web of contemporary supply chains poses significant challenges in product tracking and authenticity verification. This lack of clarity can lead to operational inefficiencies, increased fraud risk, and the proliferation of counterfeit products.",
    approach:
      "ZenithBlox offers a secure, blockchain-based infrastructure for managing, sharing, and accessing sensitive patient data across healthcare networks, ensuring end-to-end data integrity, regulatory compliance, and seamless collaboration between providers, patients, and stakeholders.ZenithBlox offers cutting-edge financial solutions tailored to tackle supply chain obstacles. Our platform enhances operational workflows, minimizes expenses, and fosters fintech advancements to promote transparency and efficiency throughout the supply chain.",
  },
  {
    id: "government",
    title: "Government",
    videoSrc: "https://zenithblox.blob.core.windows.net/videos/government.mp4?sp=r&st=2025-06-11T15:35:50Z&se=2060-06-11T23:35:50Z&spr=https&sv=2024-11-04&sr=b&sig=GCENSYDiKbjq5PZHjK6nreYu1Ff5BDvHWjlpsRuYs1g%3D",
    challenge:
      "Governments face ongoing challenges in ensuring transparency, security, and accountability in public services. Traditional systems often suffer from inefficiencies, fraud risks, and vulnerabilities that undermine trust and effectiveness.Navigating the intricate web of contemporary supply chains poses significant challenges in product tracking and authenticity verification. This lack of clarity can lead to operational inefficiencies, increased fraud risk, and the proliferation of counterfeit products.",
    approach:
      "ZenithBlox leverages advanced blockchain technology to enhance public service transparency, strengthen security measures, and ensure greater accountability across government operations and citizen interactions.ZenithBlox offers cutting-edge financial solutions tailored to tackle supply chain obstacles. Our platform enhances operational workflows, minimizes expenses, and fosters fintech advancements to promote transparency and efficiency throughout the supply chain.",
  },
];

// Component for a single Use Case block
// --- MODIFICATION: Accept 'id' prop ---
const UseCaseCard = ({ id, title, videoSrc, challenge, approach }) => (
  // --- MODIFICATION: Add 'id' to the root element ---
  <AnimatedSection
    id={id}
    tag="div"
    triggerOnce={false}
    animationVariants={fadeInUp}
    className="mb-16 md:mb-24 bg-black text-white rounded-2xl p-6 md:p-8 shadow-xl border-2 border-gray-800"
  >
    <AnimatedSection triggerOnce={false} animationVariants={fadeInLeft} threshold={0.2}>
      <h2 className="text-3xl md:text-4xl font-semibold mb-6">{title}</h2>
    </AnimatedSection>

    <AnimatedSection
      triggerOnce={false}
      animationVariants={scaleUp}
      threshold={0.2}
      className="relative aspect-video rounded-lg mb-8 overflow-hidden border border-gray-700 bg-black"
    >
      {videoSrc ? (
        <motion.video
          key={videoSrc}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-full h-full object-cover"
          src={videoSrc}
          controls
          playsInline
          muted
          loop
          preload="metadata"
        >
          Your browser does not support the video tag.
        </motion.video>
      ) : (
        <div className="w-full h-full bg-gray-800 flex items-center justify-center text-gray-400">{/* Video Placeholder */}</div>
      )}
    </AnimatedSection>

    <AnimatedSection
      triggerOnce={false}
      animationVariants={fadeIn}
      staggerChildren={0.15}
      threshold={0.2}
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <motion.div
        variants={staggerItemFadeInUp}
        whileHover={{
          scale: 1.03,
          backgroundColor: "#2A2A2A",
          borderColor: "#A855F7",
        }}
        transition={{ type: "spring", stiffness: 300, damping: 15 }}
        className="bg-[#1E1E1E] p-6 rounded-lg border border-[#1E1E1E]"
      >
        <h3 className="text-xl font-medium mb-3 text-gray-200">Critical Challenge</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{challenge}</p>
      </motion.div>

      <motion.div
        variants={staggerItemFadeInUp}
        whileHover={{
          scale: 1.03,
          backgroundColor: "#2A2A2A",
          borderColor: "#A855F7",
        }}
        transition={{ type: "spring", stiffness: 300, damping: 15 }}
        className="bg-[#1E1E1E] p-6 rounded-lg border border-[#1E1E1E]"
      >
        <h3 className="text-xl font-medium mb-3 text-gray-200">Our Approach</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{approach}</p>
      </motion.div>
    </AnimatedSection>
  </AnimatedSection>
);

const UseCasesPage = () => {
  // --- ADDITION: Logic to handle scrolling to hash links ---
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        // We use a small timeout to ensure the element is rendered before scrolling
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [hash]); // Rerun effect when hash changes
  // --- END ADDITION ---

  return (
    <div className="bg-white text-black py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="mb-16 md:mb-24 text-left">
          <h1 className="text-5xl md:text-6xl font-semibold leading-tight mb-4 text-black mt-12">
            Enterprise-proven <br /> and industry-tested.
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Discover how ZenithBlox drives business transformation, enhances security, and unlocks new efficiencies across diverse industries.
          </p>
        </AnimatedSection>

        <div>
          {useCasesData.map((useCase) => (
            // --- MODIFICATION: Pass 'id' prop ---
            <UseCaseCard
              key={useCase.id}
              id={useCase.id}
              title={useCase.title}
              videoSrc={useCase.videoSrc}
              challenge={useCase.challenge}
              approach={useCase.approach}
            />
          ))}
        </div>
      </div>
      <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} threshold={0.05} className="-mb-28">
        <ContactForm />
      </AnimatedSection>
    </div>
  );
};

export default UseCasesPage;